/**
 * Agent chat input: multiline text + send. Enter (or Cmd/Ctrl+Enter)
 * submits; Shift+Enter inserts a new line.
 *
 * The model is chosen server-side via the provider seam, so there is no
 * client model picker; connection is HTTP+SSE per request (no socket status).
 *
 * Voice dictation is opt-in via the `dictation` prop: when provided, a mic
 * button and a persisted MUI `Switch` ("Auto-send dictation", default OFF)
 * sit alongside the send button. The `dictation` ports (`recorderFactory`,
 * `transcribe`) are SYSTEM BOUNDARIES — production wires the real
 * `MediaRecorder` + `fetch`; tests pass deterministic fakes.
 */
import {
    Box,
    IconButton,
    InputBase,
    Stack,
    Switch,
    Tooltip,
    Typography,
} from '@mui/material';
import { ArrowUp, Mic, Square } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useRecoilState } from 'recoil';
import { dictationAutoSendState } from '~/state/atoms/chatAtoms';
import { useDictation, type DictationRecorder } from '~/agent';

export interface ChatInputDictationDeps {
    readonly recorderFactory: () => DictationRecorder;
    readonly transcribe: (
        audio: Uint8Array,
        mimeType: string,
        signal?: AbortSignal
    ) => Promise<{ readonly text: string }>;
}

interface ChatInputProps {
    onSend: (content: string) => void;
    disabled: boolean;
    /** When provided, the dictation UI is rendered. Omit to hide it. */
    dictation?: ChatInputDictationDeps;
}

export default function ChatInput({
    onSend,
    disabled,
    dictation,
}: ChatInputProps) {
    const [value, setValue] = useState('');
    const canSend = !disabled && value.trim().length > 0;

    const handleSend = useCallback(() => {
        const trimmed = value.trim();
        if (!trimmed || disabled) {
            return;
        }
        onSend(trimmed);
        setValue('');
    }, [value, disabled, onSend]);

    const handleKeyDown = useCallback(
        (e: React.KeyboardEvent) => {
            // Enter (or Cmd/Ctrl+Enter) sends; Shift+Enter adds a new line.
            // Never while an input method is still composing a word.
            if (e.key !== 'Enter' || e.shiftKey || e.nativeEvent.isComposing) {
                return;
            }
            e.preventDefault();
            handleSend();
        },
        [handleSend]
    );

    return (
        <Box px={0.5} pb={0.5}>
            <Box
                sx={(theme) => ({
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 1,
                    backgroundColor:
                        theme.palette.mode === 'dark'
                            ? 'rgba(255,255,255,0.04)'
                            : 'rgba(0,0,0,0.02)',
                    '&:focus-within': {
                        borderColor: theme.palette.primary.main,
                    },
                })}
            >
                <InputBase
                    fullWidth
                    multiline
                    maxRows={4}
                    placeholder="Type a message..."
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    sx={{
                        px: 1.5,
                        py: 0.75,
                        pb: 0.25,
                        fontSize: '0.85rem',
                        '& .MuiInputBase-input': { py: 0.25 },
                    }}
                />
                <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    px={0.75}
                    pb={0.5}
                    gap={1}
                >
                    {dictation ? (
                        <DictationControls
                            dictation={dictation}
                            disabled={disabled}
                            onDraft={setValue}
                            onAutoSend={onSend}
                        />
                    ) : (
                        <Box />
                    )}
                    <IconButton
                        size="small"
                        onClick={handleSend}
                        disabled={!canSend}
                        aria-label="Send message"
                        sx={(theme) => ({
                            width: 28,
                            height: 28,
                            flexShrink: 0,
                            borderRadius: '8px',
                            backgroundColor: canSend
                                ? theme.palette.primary.main
                                : 'transparent',
                            color: canSend
                                ? '#fff'
                                : theme.palette.text.disabled,
                            '&:hover': {
                                backgroundColor: canSend
                                    ? theme.palette.primary.dark
                                    : 'transparent',
                            },
                            '&.Mui-disabled': {
                                color: theme.palette.text.disabled,
                            },
                        })}
                    >
                        <ArrowUp size={16} />
                    </IconButton>
                </Box>
            </Box>
        </Box>
    );
}

interface DictationControlsProps {
    readonly dictation: ChatInputDictationDeps;
    readonly disabled: boolean;
    readonly onDraft: (text: string) => void;
    readonly onAutoSend: (text: string) => void;
}

/**
 * Mic + auto-send Switch. Lives in a sub-component so the `useDictation`
 * hook is only mounted when the caller passes dictation deps.
 */
function DictationControls({
    dictation,
    disabled,
    onDraft,
    onAutoSend,
}: DictationControlsProps) {
    const [autoSend, setAutoSend] = useRecoilState(dictationAutoSendState);
    // A transcription that resolves after the input was disabled (drawer
    // closed / assistant busy) must read the live disabled flag, not the one
    // captured when dictation started.
    const disabledRef = useRef(disabled);
    useEffect(() => {
        disabledRef.current = disabled;
    }, [disabled]);
    const dict = useDictation({
        recorderFactory: dictation.recorderFactory,
        transcribe: dictation.transcribe,
        onText: (text) => {
            // Never auto-send once disabled: surface it as an editable draft.
            if (autoSend && !disabledRef.current) {
                onAutoSend(text);
            } else {
                onDraft(text);
            }
        },
    });

    const toggleMic = useCallback((): void => {
        if (dict.recording) {
            void dict.stop();
        } else {
            void dict.start();
        }
    }, [dict]);

    const micBusy = dict.recording || dict.busy;
    const micLabel = dict.recording
        ? 'Stop dictation'
        : dict.busy
          ? 'Transcribing dictation'
          : 'Start dictation';

    return (
        <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{ minWidth: 0 }}
        >
            <Tooltip title={micLabel}>
                <span>
                    <IconButton
                        size="small"
                        onClick={toggleMic}
                        disabled={disabled || dict.busy}
                        aria-label={micLabel}
                        sx={(theme) => ({
                            width: 28,
                            height: 28,
                            color: dict.recording
                                ? theme.palette.error.main
                                : theme.palette.text.secondary,
                        })}
                    >
                        {dict.recording ? (
                            <Square size={14} />
                        ) : (
                            <Mic size={14} />
                        )}
                    </IconButton>
                </span>
            </Tooltip>
            <Switch
                checked={autoSend}
                onChange={(e) => setAutoSend(e.target.checked)}
                disabled={micBusy}
                inputProps={{ 'aria-label': 'Auto-send dictation' }}
            />
            <Typography
                component="label"
                variant="caption"
                sx={{
                    fontSize: 11,
                    lineHeight: 1,
                    color: 'text.secondary',
                    userSelect: 'none',
                }}
                onClick={() => !micBusy && setAutoSend((v) => !v)}
            >
                Auto-send
            </Typography>

            {dict.error && (
                <Typography
                    variant="caption"
                    color="error.main"
                    sx={{
                        ml: 0.5,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {dict.error}
                </Typography>
            )}
        </Stack>
    );
}
